import { useState } from 'react';
import { cn } from '../../lib/utils';
import { lightGreenBG, greenTextColor, secondaryText, whiteBG, borderPrimary, smallTextSize, semiBoldText } from '../../styling/stylingHelper';
import { DropdownSelector } from './DropdownSelector';

export interface FilterOption {
  value: string; // e.g., 'all', 'section1'
  label: string; // e.g., 'All sections'
}

interface TabbedSectionsProps {
  className?: string;
  options: FilterOption[];
  defaultValue?: string;
  value?: string; // Controlled value prop
  onChange?: (value: string) => void;
  isTimeOptionSelection: boolean;
  maxTabsBeforeDropdown?: number; // Max number of tabs before switching to dropdown (default: 3)
  /** When true, tabs stretch to fill the full container width equally. */
  fullWidth?: boolean;
  /** Optional anchor for the whole control, including its dropdown layout. */
  onboardingId?: string;
}

export const TabbedSections = ({
  className,
  options,
  defaultValue,
  value,
  onChange,
  isTimeOptionSelection = false,
  maxTabsBeforeDropdown = 3,
  fullWidth = false,
  onboardingId,
}: TabbedSectionsProps) => {
  const [internalValue, setInternalValue] = useState(defaultValue || options[0]?.value || '');

  // Use controlled value if provided, otherwise use internal state
  const selectedValue = value !== undefined ? value : internalValue;

  const handleSelect = (newValue: string) => {
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const useDropdown = !isTimeOptionSelection && options.length > maxTabsBeforeDropdown;

  return (
    <div data-onboarding={onboardingId} className={cn(fullWidth ? "w-full" : "w-full min-[950px]:w-fit", smallTextSize, semiBoldText, className)}>
      {/* Desktop tabs/dropdown: visible above 950px */}
      <div className="hidden min-[950px]:block">
        {useDropdown ? (
          <DropdownSelector
            options={options}
            defaultValue={selectedValue}
            onChange={handleSelect}
          />
        ) : (
          <div className={cn(fullWidth ? "flex w-full" : "inline-flex")}>
            {options.map((opt, idx) => {
              const isSelected = selectedValue === opt.value;
              const bgClass = isSelected ? lightGreenBG : whiteBG;
              const textClass = isSelected ? greenTextColor : secondaryText;
              const topBottomBorder = `border-t ${borderPrimary} border-b ${borderPrimary}`;
              const leftRounded = idx === 0 ? 'rounded-l-xl' : '';
              const rightRounded = idx === options.length - 1 ? 'rounded-r-xl' : '';
              const dividerBorder = idx < options.length - 1 ? `border-r border-gray-300` : '';
              const leftEndBorder = idx === 0 ? `border-l ${borderPrimary}` : '';
              const rightEndBorder = idx === options.length - 1 ? `border-r ${borderPrimary}` : '';

              return (
                <button
                  data-onboarding={onboardingId ? `${onboardingId}-${opt.value}` : undefined}
                  key={opt.value}
                  type="button"
                  onClick={() => handleSelect(opt.value)}
                  className={cn(
                    'inline-flex items-center px-3 py-2',
                    fullWidth && 'flex-1 justify-center',
                    bgClass,
                    textClass,
                    topBottomBorder,
                    dividerBorder,
                    leftEndBorder,
                    rightEndBorder,
                    leftRounded,
                    rightRounded,
                  )}
                  style={fullWidth ? undefined : { flex: '0 0 auto', minWidth: 'unset' }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile dropdown: visible below 950px - always DropdownSelector */}
      <div className="block min-[950px]:hidden">
        <DropdownSelector
          options={options}
          defaultValue={selectedValue}
          onChange={handleSelect}
        />
      </div>
    </div>
  );
};
